import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type AffiliateLink = {
  id: string;
  name: string;
  url: string;
  brand: string;
  description: string | null;
  discountCode: string | null;
  categoryId: string;
  mediaId: string | null;
  priceLabel: string | null;
  rating: number | null;
  tag: string | null;
  keywords: string | null;
  featured: boolean;
  sortOrder: number;
};
type Category = { id: string; name: string };
type AffiliateSortKey = "name" | "brand" | "url" | "sort";

type FormState = {
  name: string;
  url: string;
  brand: string;
  description: string;
  discountCode: string;
  categoryId: string;
  mediaId: string;
  priceLabel: string;
  rating: string;
  tag: string;
  keywords: string;
  featured: boolean;
  sortOrder: string;
};

const empty: FormState = {
  name: "",
  url: "",
  brand: "",
  description: "",
  discountCode: "",
  categoryId: "",
  mediaId: "",
  priceLabel: "",
  rating: "",
  tag: "",
  keywords: "",
  featured: false,
  sortOrder: "0",
};

function toForm(l: AffiliateLink): FormState {
  return {
    name: l.name,
    url: l.url,
    brand: l.brand,
    description: l.description ?? "",
    discountCode: l.discountCode ?? "",
    categoryId: l.categoryId,
    mediaId: l.mediaId ?? "",
    priceLabel: l.priceLabel ?? "",
    rating: l.rating?.toString() ?? "",
    tag: l.tag ?? "",
    keywords: l.keywords ?? "",
    featured: l.featured,
    sortOrder: l.sortOrder.toString(),
  };
}

function affiliateSearchText(link: AffiliateLink, categoryName: string) {
  return [
    link.name,
    link.brand,
    link.url,
    link.description,
    link.discountCode,
    categoryName,
    link.priceLabel,
    link.rating == null ? "" : String(link.rating),
    link.tag,
    link.keywords,
    link.featured ? "featured" : "",
    formatInteger(link.sortOrder),
  ]
    .filter(Boolean)
    .join(" ");
}

const affiliateSorters: Record<AffiliateSortKey, (row: AffiliateLink) => string | number> = {
  name: (row) => row.name,
  brand: (row) => row.brand,
  url: (row) => row.url,
  sort: (row) => row.sortOrder,
};

export default function AdminAffiliateLinks() {
  const [list, setList] = useState<AffiliateLink[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [editing, setEditing] = useState<AffiliateLink | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ affiliateLinks: AffiliateLink[] }>("/api/admin/affiliate-links");
    setList(r.affiliateLinks);
  }

  useEffect(() => {
    Promise.all([
      refresh(),
      api.get<{ categories: Category[] }>("/api/admin/categories").then((r) => setCats(r.categories)),
    ]).finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm({ ...empty, categoryId: cats[0]?.id ?? "" });
    setEditing("new");
    setErr(null);
  }
  function startEdit(l: AffiliateLink) {
    setForm(toForm(l));
    setEditing(l);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      name: form.name,
      url: form.url,
      brand: form.brand,
      categoryId: form.categoryId,
      description: form.description || null,
      discountCode: form.discountCode || null,
      mediaId: form.mediaId || null,
      priceLabel: form.priceLabel || null,
      rating: form.rating ? parseInt(form.rating, 10) : null,
      tag: form.tag || null,
      keywords: form.keywords || null,
      featured: form.featured,
      sortOrder: parseInt(form.sortOrder || "0", 10),
    };
    try {
      if (editing === "new") await api.post("/api/admin/affiliate-links", payload);
      else if (editing) await api.put(`/api/admin/affiliate-links/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(l: AffiliateLink) {
    if (!confirm(`Delete "${l.name}"? This cannot be undone.`)) return;
    await api.del(`/api/admin/affiliate-links/${l.id}`);
    await refresh();
  }

  const categoryById = useMemo(() => new Map(cats.map((cat) => [cat.id, cat.name])), [cats]);
  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All categories" },
      ...cats.map((cat) => ({ value: cat.id, label: cat.name })),
    ],
    [cats],
  );
  const filters = useMemo(
    () => [
      (row: AffiliateLink) => categoryFilter === "all" || row.categoryId === categoryFilter,
      (row: AffiliateLink) =>
        featuredFilter === "all" ||
        (featuredFilter === "featured" && row.featured) ||
        (featuredFilter === "standard" && !row.featured),
    ],
    [categoryFilter, featuredFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<AffiliateLink, AffiliateSortKey>({
    rows: list,
    search,
    initialSort: { key: "sort", direction: "asc" },
    sorters: affiliateSorters,
    searchText: (row) => affiliateSearchText(row, categoryById.get(row.categoryId) ?? ""),
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Affiliate Links</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New affiliate link
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New affiliate link" : editing.name}
            eyebrow={editing === "new" ? "Create affiliate link" : "Edit affiliate link"}
            backLabel="Back to affiliate links"
            onBack={() => setEditing(null)}
          />

          <div className="grid2">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} required />
          </div>
          <Field label="Affiliate URL" value={form.url} onChange={(v) => setForm({ ...form, url: v })} required />
          <Field
            label="Description"
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            textarea
          />

          <div className="grid2">
            <label className="fld">
              <span>Category</span>
              <select
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label="Media ID (optional)"
              value={form.mediaId}
              onChange={(v) => setForm({ ...form, mediaId: v })}
            />
          </div>

          <div className="grid2">
            <Field
              label="Price label"
              value={form.priceLabel}
              onChange={(v) => setForm({ ...form, priceLabel: v })}
            />
            <Field
              label="Discount code"
              value={form.discountCode}
              onChange={(v) => setForm({ ...form, discountCode: v })}
            />
          </div>

          <div className="grid2">
            <Field label="Tag" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} />
            <Field
              label="Rating (1–5)"
              value={form.rating}
              onChange={(v) => setForm({ ...form, rating: v })}
            />
          </div>

          <Field
            label="Keywords"
            value={form.keywords}
            onChange={(v) => setForm({ ...form, keywords: v })}
          />

          <div className="grid2">
            <Field
              label="Sort order"
              value={form.sortOrder}
              onChange={(v) => setForm({ ...form, sortOrder: v })}
            />
            <label className="fld checkbox">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              />
              <span style={{ marginLeft: 8 }}>Featured</span>
            </label>
          </div>

          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search affiliate links, brands, codes..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categoryOptions}
            />
            <AdminTableFilterSelect
              label="Featured"
              value={featuredFilter}
              onChange={setFeaturedFilter}
              options={[
                { value: "all", label: "All links" },
                { value: "featured", label: "Featured" },
                { value: "standard", label: "Not featured" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No affiliate links yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No affiliate links match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={requestSort} />
                  <SortableTh label="Brand" sortKey="brand" sort={sort} onSort={requestSort} />
                  <SortableTh label="URL" sortKey="url" sort={sort} onSort={requestSort} />
                  <SortableTh label="Sort" sortKey="sort" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <strong>{l.name}</strong>
                      {l.tag && <span className="slug">{l.tag}</span>}
                      {l.featured && <span className="slug">featured</span>}
                    </td>
                    <td>{l.brand}</td>
                    <td>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: colors.berryRose, fontFamily: fonts.body, fontSize: 13 }}
                      >
                        {l.url.length > 48 ? l.url.slice(0, 48) + "..." : l.url}
                      </a>
                    </td>
                    <td>{formatInteger(l.sortOrder)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(l)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(l)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld textarea {
    width: 100%; min-height: 90px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 500; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld.checkbox { display: flex; align-items: center; padding-top: 1.6rem; }
  .fld.checkbox input { accent-color: ${colors.berryRose}; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
`;
